import cn from 'classnames'
import {
  Beta,
  IconChevronLeftDefault,
  IconChevronRightDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { ALL_CLASSES } from 'libs/utils/categories'
import { CategoryNavItemWrapper } from './CategoryNavItem/CategoryNavItemWrapper'
import type { Category } from './types'
import classes from './CategoryNavCarousel.module.scss'

type Props = {
  categories: Category[]
  slug?: string
  pathCategory: (slug: string) => string
  onNavClick?: (category: Category) => void
}

export const CategoryNavCarousel = ({ categories, ...props }: Props) => {
  const { t } = useTranslation('@mc/next')

  if (!categories) return <></>

  return (
    <Beta.Carousel srLabel={t('nav.categoryNav.a11y.categoryNavCarousel')}>
      <CategoryNavCarouselContent categories={categories} {...props} />
    </Beta.Carousel>
  )
}

const CategoryNavCarouselContent = ({
  categories,
  slug,
  pathCategory,
  onNavClick,
}: Props) => {
  const { start, end } = Beta.useCarousel()
  const catsToDisplay = useCategories(categories)

  return (
    <div
      className={cn(
        'd-flex align-items-center',
        'uncontainer',
        classes.container,
      )}
    >
      <div
        className={cn(
          'position-relative',
          classes.navButtons,
          classes.gradient,
          start && classes.start,
          end && classes.end,
        )}
      >
        <Beta.CarouselContent className={classes.content}>
          {catsToDisplay.map((category) => (
            <Beta.CarouselItem key={category.slug}>
              <CategoryNavItemWrapper
                category={category}
                slug={slug}
                pathCategory={pathCategory}
                onNavClick={onNavClick}
              />
            </Beta.CarouselItem>
          ))}
        </Beta.CarouselContent>
      </div>
      <div className='d-none d-sm-flex flex-row'>
        <div className='mc-m-1 mc-ml-2'>
          <Beta.CarouselPrevious kind='secondary' size='xs' symmetrical rounded>
            <IconChevronLeftDefault />
          </Beta.CarouselPrevious>
        </div>
        <div className='mc-m-1 mc-mr-0'>
          <Beta.CarouselNext kind='secondary' size='xs' symmetrical rounded>
            <IconChevronRightDefault />
          </Beta.CarouselNext>
        </div>
      </div>
    </div>
  )
}

const useCategories = (categories: Category[]) => {
  const { t } = useTranslation('@mc/next')

  const allCatsOption = {
    id: 0,
    slug: ALL_CLASSES,
    name: t('nav.categoryNav.all'),
  }

  return [allCatsOption, ...categories]
}
